<template>
  <div>
    <div class="f-inline-group">
      <legend
        class="f-field__label"
      >
        {{ $t(`fields.record.${fieldName}.label`) }}
      </legend>
      <div
        v-if="$te(`advancedsearch.fields.${fieldName}.description`)"
        class="f-field__tooltip"
      >
        <Tooltip>
          <p>
            {{ $t(`advancedsearch.fields.${fieldName}.description`) }}
          </p>
        </Tooltip>
      </div>
    </div>
    <div class="f-field">
      <div
        v-if="option"
        class="f-inline-group"
      >
        <div class="f-field-advanced-search__controller-select">
          <select
            v-model="option"
            class="f-field__input f-field__input--select"
            @change="changeOption"
          >
            <optgroup
              v-for="optGroup in [{'label': 'users', opts: ['userId', 'affiliation']}, {'label':'names', opts: ['all', 'any', 'none']}]"
              :key="fieldName + optGroup.label"
              :label="getOptionString(optGroup.label)"
            >
              <option
                v-for="opt in optGroup.opts"
                :key="fieldName + opt"
                :value="opt"
              >
                {{ getOptionString(opt) }}
              </option>
            </optgroup>
          </select>
        </div>
        <TextInputField
          v-if="['all', 'any', 'none'].includes(option)"
          v-model="currentSignature"
          :field-name="`record.${fieldName}`"
          :is-required="false"
          :show-label="false"
        />
        <template v-else-if="option === 'userId'">
          <div
            v-if="currentAuthor"
            class="user-search"
          >
            <UserTag
              class="layout-user-tag user-tag--adv-search"
              :user="currentAuthor"
              @removeuser="unassignUser"
            />
          </div>
          <div
            v-else
            class="user-search"
          >
            <ResearcherPicker
              v-model="currentAuthor"
              field-name="fields.record.authorPicker"
              @user-selected="assignUser"
            />
          </div>
        </template>
        <AffiliationPicker
          v-if="option === 'affiliation'"
          v-model="currentAffiliations"
          :units-list="allUnits"
          :start-open="true"
          @change="updateValue"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { MetadataUtils } from '@scholar/metadata-utils/main'
import ResearcherPicker from '#src/components/utils/fields/ResearcherPicker.vue'
import UserTag from '#src/components/utils/UserTag.vue'
import TextInputField from '#src/components/utils/fields/TextInputField.vue'
import useResearchersAPI from '#src/api/researchers'
import useUnitsAPI from '#src/api/units'
import { fetchAllPages } from '#src/utils/pagination-helpers'
import Tooltip from '#src/components/utils/Tooltip.vue'
import AffiliationPicker from '#src/components/advanced-search/fields/AffiliationPicker.vue'

export default {
  components: {
    ResearcherPicker,
    UserTag,
    TextInputField,
    Tooltip,
    AffiliationPicker
  },
  inject: ['$axios'],
  /**
   * value fields depend on the current option:
   * { option: 'userId', value: { name: ... , userId: ..., affiliations?: [...]}, display: { userProfile: ..., affiliations?: [...] } }
   * { option: 'all'|'any'|'none', value: { name: ... }, display: { name: ... } }
   * { option: 'affiliations', value: { affiliations: [...] }, display: { affiliations: [...] } }
   */
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    fieldName: {
      type: String,
      required: true
    },
    recordType: {
      type: String,
      required: true
    },
    error: {
      type: String,
      default: null
    }
  },
  emits: ['change', 'isValid'],
  data() {
    return {
      ResearchersAPI: useResearchersAPI(this.$axios),
      UnitsAPI: useUnitsAPI(this.$axios),
      allUnits: [],
      unitsList: [],
      option: 'userId',
      currentAuthor: null,
      currentSignature: '',
      currentAffiliations: [],
      addingAffiliation: false,
      validation: { status: true },
      parser: new MetadataUtils().getParserByType(this.recordType)
    }
  },
  computed: {
    inputError() {
      if (this.error) {
        return { type: 'danger', message: this.$t(`forms.fields.errors.metadata-utils.${this.error}`) }
      } else {
        return this.validation.status ? {} : { type: 'danger', message: this.$t(`forms.fields.errors.metadata-utils.${this.validation?.field?.error}`) }
      }
    }
  },
  watch: {
    // TODO: maybe just emit @change from TextInputField instead of watching the value
    currentSignature() {
      this.updateValue()
    }
  },
  async created() {
    this.allUnits = (await fetchAllPages(page => this.UnitsAPI.listUnits({ page, perPage: 20 }))).items
    this.option = this.modelValue.option ?? this.option

    // assignUser destroys affiliations
    const affiliations = this.modelValue.value?.affiliations

    if (this.modelValue.value?.name) {
      this.currentSignature = this.modelValue.value.name
    }
    if (this.modelValue.value?.userId) {
      const user = await this.ResearchersAPI.get({ userId: this.modelValue.value.userId })
      await this.assignUser(user, false)
    }
    if (affiliations) {
      this.currentAffiliations = affiliations.map(id => this.allUnits.find(aff => aff.id === id))
    }

    this.updateValue()
  },
  methods: {
    async updateUnitsList(userId) {
      // TODO: this should be sorted or something?
      const affiliationsPage = await fetchAllPages(page => this.UnitsAPI.getAffiliations({ userId, page, perPage: 20 }))
      this.unitsList = []
      const units = {}
      for (const affiliation of affiliationsPage.items) {
        if (!units[affiliation.unit.id]) {
          units[affiliation.unit.id] = affiliation.id
          this.unitsList.push(affiliation.unit)
        }
      }
    },
    async assignUser(user, update = true) {
      this.currentAuthor = user

      this.currentAffiliations = []
      await this.updateUnitsList(user.id)

      if (update) {
        this.updateValue()
      }
    },
    unassignUser() {
      this.currentAuthor = null
      this.affiliations = []
      this.updateValue()
    },
    setResearcherField(val) {
      this.option = val
      this.updateValue()
    },
    changeOption() {
      this.currentSignatures = []
      this.currentAuthor = null
      this.currentAffiliations = []

      this.updateValue()
    },
    updateValue() {
      const value = {}
      const display = {}

      if (this.option === 'userId' && this.currentAuthor) {
        value.userId = this.currentAuthor.id
        display.userProfile = this.currentAuthor.name
      } else if (['all', 'any', 'none'].includes(this.option)) {
        value.name = this.currentSignature
        display.name = this.currentSignature
      }
      if (this.currentAffiliations.length > 0) {
        value.affiliations = this.currentAffiliations.map(aff => aff.id)
        display.affiliations = this.currentAffiliations.map(aff => aff.name)
      }
      this.modelValue.option = this.option
      this.modelValue.value = value
      this.modelValue.display = display
      this.$emit('change', this.modelValue)
    },
    async validate() {
      // TODO: what verifications are needed here?
      this.$emit('isValid', this.validation.status)
      this.modelValue.validation = this.validation.status
    },
    getOptionString(opt) {
      return ['all', 'any', 'none'].includes(opt)
        ? this.$t(`advancedsearch.fields.string.options.${opt}`)
        : this.$t(`advancedsearch.fields.user.options.${opt}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.user-search {
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  gap: 0.5rem;
  align-items: flex-start;
  justify-content: flex-start;

  .f-field {
    margin: 0;
    padding-left: 0;
  }
}
</style>
